import React, { useState } from 'react'
import styles from './MicrositeDeleteConfirm.module.css'
import { Trash2, AlertTriangle, X } from 'lucide-react'

/**
 * MicrositeDeleteConfirm
 *
 * Two render modes for the same soft-delete confirmation pattern,
 * used across every list screen (Redirects, SEO Pages, Custom Pages,
 * Blog, Popups, Announcements, Tracking Scripts, Domains, Email
 * Templates, Versions). Nothing here is ever a hard delete — the
 * parent's onConfirm should call the relevant micrositeDeleteX API,
 * which soft-deletes (isDeleted=1) on the backend.
 *
 * Mode 1 — inline (small icon button that expands a tiny confirm row,
 * good for table row actions):
 *   <MicrositeDeleteConfirm
 *     mode="inline"
 *     onConfirm={() => handleDelete(row.ID)}
 *   />
 *
 * Mode 2 — modal (for editor screens' "Delete" header button):
 *   <MicrositeDeleteConfirm
 *     mode="modal"
 *     open={confirmOpen}
 *     onClose={() => setConfirmOpen(false)}
 *     onConfirm={handleDelete}
 *     itemLabel="this blog post"
 *   />
 */
const MicrositeDeleteConfirm = ({
    mode = 'inline',
    open,
    onClose,
    onConfirm,
    itemLabel = 'this item',
    title,
    description
}) => {
    const [inlineConfirming, setInlineConfirming] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const handleConfirm = async () => {
        setDeleting(true)
        await onConfirm?.()
        setDeleting(false)
        setInlineConfirming(false)
    }

    if (mode === 'inline') {
        if (!inlineConfirming) {
            return (
                <button
                    type="button"
                    className={styles.inlineTriggerBtn}
                    onClick={(e) => {
                        e.stopPropagation()
                        setInlineConfirming(true)
                    }}
                    title="Delete"
                >
                    <Trash2 size={14} />
                </button>
            )
        }

        return (
            <span className={styles.inlineConfirmRow} onClick={(e) => e.stopPropagation()}>
                <span className={styles.inlineConfirmText}>Delete?</span>
                <button
                    type="button"
                    className={styles.inlineConfirmYes}
                    onClick={handleConfirm}
                    disabled={deleting}
                >
                    {deleting ? '…' : 'Yes'}
                </button>
                <button
                    type="button"
                    className={styles.inlineConfirmNo}
                    onClick={() => setInlineConfirming(false)}
                    disabled={deleting}
                >
                    No
                </button>
            </span>
        )
    }

    // ── modal mode ──
    if (!open) return null

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <button type="button" className={styles.closeBtn} onClick={onClose}>
                    <X size={15} />
                </button>

                <div className={styles.iconWrap}>
                    <AlertTriangle size={20} />
                </div>

                <h3 className={styles.title}>{title || `Delete ${itemLabel}?`}</h3>
                <p className={styles.description}>
                    {description || `This will remove ${itemLabel} from your microsite. This action can be undone by Lokaci support if needed.`}
                </p>

                <div className={styles.actions}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={deleting}>
                        Cancel
                    </button>
                    <button type="button" className={styles.confirmBtn} onClick={handleConfirm} disabled={deleting}>
                        <Trash2 size={13} />
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default MicrositeDeleteConfirm